/**
 * Spoken commands for voice mode (pure): "what's happening", "tell me more about Viko",
 * "answer the first question: yes", "let's discuss Zip Mail …", "wrap up", "stop". Inside a
 * discussion anything that is not a command is said to the project's agent verbatim.
 */

export interface NamedProject {
  id: string
  name: string
}

export type VoiceIntent =
  | { kind: 'overview' }
  | { kind: 'project'; projectId: string }
  | { kind: 'unknown-project'; name: string }
  | { kind: 'asks' }
  /** `index` is 0-based; -1 is "the last one". */
  | { kind: 'answer'; index: number; answer: string }
  | { kind: 'approve'; index: number; decision: 'allow' | 'deny' }
  | { kind: 'discuss'; projectId: string; text: string }
  | { kind: 'distill' }
  | { kind: 'end' }
  | { kind: 'cancel' }
  | { kind: 'repeat' }
  | { kind: 'help' }
  | { kind: 'say'; text: string }
  | { kind: 'unknown'; text: string }

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[’`]/g, "'")
    .replace(/[^\p{L}\p{N}\s']/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()

function editDistance(a: string, b: string): number {
  const dp = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    let prev = dp[0]!
    dp[0] = i
    for (let j = 1; j <= b.length; j++) {
      const cur = dp[j]!
      dp[j] = Math.min(dp[j]! + 1, dp[j - 1]! + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1))
      prev = cur
    }
  }
  return dp[b.length]!
}

/** Best project for a spoken name: exact, then prefix/contains, then a small edit distance. */
export function matchProject<P extends NamedProject>(spoken: string, projects: readonly P[]) {
  const n = norm(spoken)
  if (!n) return undefined
  const named = projects.map((p) => ({ p, k: norm(p.name) }))
  const exact = named.find((x) => x.k === n)
  if (exact) return exact.p
  const partial = named
    .filter((x) => x.k.startsWith(n) || n.startsWith(x.k) || x.k.includes(n))
    .sort((a, b) => Math.abs(a.k.length - n.length) - Math.abs(b.k.length - n.length))
  if (partial[0]) return partial[0].p
  let best: { p: P; d: number } | undefined
  for (const x of named) {
    const d = Math.min(editDistance(n, x.k), ...x.k.split(' ').map((w) => editDistance(n, w)))
    const limit = Math.max(1, Math.floor(x.k.length / 4))
    if (d <= limit && (!best || d < best.d)) best = { p: x.p, d }
  }
  return best?.p
}

const ORDINALS: Record<string, number> = {
  first: 0,
  '1st': 0,
  one: 0,
  '1': 0,
  second: 1,
  '2nd': 1,
  two: 1,
  '2': 1,
  third: 2,
  '3rd': 2,
  three: 2,
  '3': 2,
  fourth: 3,
  '4th': 3,
  four: 3,
  '4': 3,
  fifth: 4,
  '5th': 4,
  five: 4,
  '5': 4,
  last: -1,
}
const ORD = Object.keys(ORDINALS).join('|')

const OVERVIEW =
  /^(what'?s|what is|whats) (happening|going on|new|up)\b|^(catch me up|status|give me (the )?(status|overview|summary)|overview|summary|how are (things|we doing))\b/
const CANCEL = /^(stop|cancel|never ?mind|quiet|be quiet|shush|enough)$/
const REPEAT = /^(repeat( that)?|say (that|it) again|again|come again|pardon)$/
const HELP = /^(help|what can (i|you) (say|do))$/
const ASKS =
  /^(what are the questions|read (me )?the questions|(any |the )?questions|what needs me|what do you need from me|read (me )?the asks)$/
const DISTILL =
  /^(wrap (it )?up|distill( this| it)?|summari[sz]e (the )?(outcomes?|discussion)|let'?s wrap up|turn (this|that) into (tasks|a plan))$/
const END =
  /^(end|stop|finish|close|leave) (the |this )?(discussion|conversation)$|^(we'?re|i'?m) done$|^that'?s all$/
const PROJECT =
  /^(?:tell me (?:more )?about|more about|what about|how is|how'?s|dive into|drill into|open|go to|switch to) (?:the )?(?:project )?(?<name>.+?)(?: project)?$/
const ANSWER = new RegExp(
  `^answer (?:the )?(?:(?<a>${ORD}) (?:question|one)|(?:question|number) (?<b>${ORD}))(?: with| as)? (?<answer>.+)$`,
)
const APPROVE = new RegExp(
  `^(?<verb>approve|allow|deny|reject|decline) (?:the )?(?<ord>${ORD}) ?(?:one|request|approval)?$`,
)
const DISCUSS =
  /^(?:let'?s )?(?:discuss|talk (?:to|with|about)|scope(?: (?:a|the) (?:feature|project|idea))?(?: with| for| in)?|brainstorm(?: with)?|plan(?: with)?) (?<rest>.+)$/

/** Split "Viko: the topic" / "Viko about the topic" into a known project and the rest. */
function projectAndTopic(rest: string, projects: readonly NamedProject[], raw: string) {
  const colon = raw.indexOf(':')
  if (colon > 0) {
    const head = raw.slice(0, colon).replace(/^.*?\b(discuss|with|about|for|in)\b/i, '')
    const p = matchProject(head, projects)
    if (p) return { p, text: raw.slice(colon + 1).trim() }
  }
  const words = rest.split(' ')
  // Longest leading run of words that names a project; the remainder is the topic.
  for (let n = Math.min(words.length, 4); n >= 1; n--) {
    const head = words.slice(0, n).join(' ')
    const p =
      projects.find((x) => norm(x.name) === head) ??
      (n === 1 ? matchProject(head, projects) : undefined)
    if (p) {
      const text = words
        .slice(n)
        .join(' ')
        .replace(/^(about|on|regarding)\s+/, '')
      return { p, text }
    }
  }
  return undefined
}

export function parseIntent(
  raw: string,
  ctx: { projects: readonly NamedProject[]; discussing: boolean },
): VoiceIntent {
  const t = norm(raw)
  if (CANCEL.test(t)) return { kind: 'cancel' }
  if (REPEAT.test(t)) return { kind: 'repeat' }
  if (HELP.test(t)) return { kind: 'help' }
  if (OVERVIEW.test(t)) return { kind: 'overview' }
  if (ASKS.test(t)) return { kind: 'asks' }
  const a = ANSWER.exec(t)
  if (a) {
    const g = a.groups!
    const ord = (g.a ?? g.b)!
    // Keep the answer's own casing from the raw text.
    const answerNorm = g.answer!.trim()
    const at = raw.toLowerCase().lastIndexOf(answerNorm.split(' ')[0]!)
    const answer =
      at >= 0
        ? raw
            .slice(at)
            .replace(/[.!?]+$/, '')
            .trim()
        : answerNorm
    return { kind: 'answer', index: ORDINALS[ord]!, answer }
  }
  const ap = APPROVE.exec(t)
  if (ap)
    return {
      kind: 'approve',
      index: ORDINALS[ap.groups!.ord!]!,
      decision: /^(approve|allow)$/.test(ap.groups!.verb!) ? 'allow' : 'deny',
    }
  if (ctx.discussing) {
    if (DISTILL.test(t)) return { kind: 'distill' }
    if (END.test(t)) return { kind: 'end' }
    return { kind: 'say', text: raw.trim() }
  }
  const d = DISCUSS.exec(t)
  if (d) {
    const hit = projectAndTopic(d.groups!.rest!, ctx.projects, raw)
    if (hit) {
      const text = hit.text && raw.includes(':') ? raw.slice(raw.indexOf(':') + 1).trim() : hit.text
      return { kind: 'discuss', projectId: hit.p.id, text }
    }
  }
  const p = PROJECT.exec(t)
  if (p) {
    const name = p.groups!.name!.trim()
    const hit = matchProject(name, ctx.projects)
    return hit ? { kind: 'project', projectId: hit.id } : { kind: 'unknown-project', name }
  }
  if (DISTILL.test(t)) return { kind: 'distill' }
  return { kind: 'unknown', text: raw.trim() }
}

export const VOICE_HELP =
  'You can say: what’s happening; tell me more about a project; read the questions; answer the ' +
  'first question, then your answer; approve or deny the first one; let’s discuss a project, then ' +
  'the topic; wrap up, to turn a discussion into proposals; say that again; or stop.'
